import React, { useState, useEffect, useCallback } from 'react';
import { matchPath, useLocation } from 'react-router';
import { useSelector, useDispatch } from 'react-redux';
import { Grid, Box, FormControl, Select, MenuItem, InputLabel, Chip, Button, LinearProgress, FormControlLabel, Checkbox } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { ResponsiveContainer, Legend, LineChart , Line, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip } from 'recharts';

import { stationListRequest, stationHistoryRequest } from '../actions/stationQueryAction';

const useStyles = makeStyles((theme) => ({
    select: {
        paddingTop: theme.spacing(2),
        backgroundColor: 'white'
    },
    selectMenu: {
        fontSize: '1rem',
        paddingTop: theme.spacing(1),
        paddingBottom: theme.spacing(1)
    },
    chips: {
        display: 'flex',
        flexWrap: 'wrap',
    },
}));

const range = (start, count) => {
    return Array(count).fill().map((_, idx) => start + idx)
}

const factors = {
    Energy: '平均發電量',
    Pyrano: '平均日照量',
    Thermo: '平均溫度',
    Temperature: '變流器平圴溫度',
    DCV: '直流平均電壓',
    DCA: '直流平均電流',
    DCW: '直流平均功率',
    ACV: '交流平均電壓',
    ACA: '交流平均電流',
    ACW: '交流平均功率',
    COFF: '變流器效率',
    AvgCOFF: '變流器累積效率',
    AvgDUST: '落塵量',
    AvgRA: 'RA值',
    AvgPR: 'PR值',
};

const factors_unit = {
    Energy: 'KW',
    Pyrano: '',
    Thermo: '°C',
    Temperature: '°C',
    DCV: 'V',
    DCA: 'A',
    DCW: 'KW',
    ACV: 'V',
    ACA: 'A',
    ACW: 'KW',
    COFF: '%',
    AvgCOFF: '%',
    AvgDUST: '%',
    AvgRA: '%',
    AvgPR: '%',
};

const colorMap = ['DEEPSKYBLUE', 'ROSYBROWN', 'YELLOWGREEN', 'MEDIUMPURPLE', 'GOLD', 'INDIANRED', 'CADETBLUE', 'MAROON', 'OLIVE', 'ROYALBLUE',
    'GRAY', 'TEAL', 'PURPLE', 'MAROON', 'SILVER', 'NAVY'];

const daysOfMonth = (year, month) => {
    return new Date(year, month, 0).getDate();
}

const now = new Date();
const yearOptions = range(now.getFullYear() - 4, 5);
const monthOptions = range(1, 12);
var dayOptions = range(1, daysOfMonth(now.getFullYear(), now.getMonth() + 1));;

const genDays = (year, month) => {
    dayOptions = range(1, daysOfMonth(year, month));
};

var drawType = 0;

const StationQuery = () => {
    const style = useStyles();
    const stationList = useSelector(state => state.stationList);
    const stationHistory = useSelector(state => state.stationHistory);
    const dispatch = useDispatch();
    const [stations, setStations] = useState([]);
    const [inverters, setInverters] = useState([]);
    const [selectStation, setSelectStation] = useState({
        station: -1,
        inverter: '',
    });
    const [selectDate, setSelectDate] = useState({
        year: yearOptions[yearOptions.length - 1],
        month: now.getMonth() + 1,
        day: 0
    });
    const [selectColumn, setSelectColumn] = useState(['Energy']);
    const [drawData, setDrawData] = useState([]);

    const location = useLocation();
    const match = matchPath(location.pathname, {
        path: "*station/:id",
        exact: true,
        strict: true
    });

    useEffect(() => {
        if (match && match.isExact) {
            if (match.params && match.params['id']) {
                dispatch(stationHistoryRequest(match.params['id'], {
                    year: yearOptions[yearOptions.length - 1],
                    month: now.getMonth() + 1,
                    day: now.getDate(),
                    columns: ['Energy']
                }));
                setSelectStation({ station: match.params['id'], inverter: -1 });

                setSelectDate({
                    year: yearOptions[yearOptions.length - 1],
                    month: now.getMonth() + 1,
                    day: now.getDate(),
                    columns: ['Energy']
                });
            }
        }
    }, []);

    useEffect( () => {

        if (selectStation.station !== -1 && stations) {
            let invs = stations.find( x => x.ID === Number(selectStation.station));
          if (invs)  setInverters(invs.inverters);
//            console.log('change', selectStation.station, invs);
        }
    }, [selectStation.station]);

    useEffect(() => {
        if (stationList && stationList.stations && stationList.stations.length > 0) {

            setStations(stationList.stations);
            if (selectStation.station === -1) {
                setInverters(stationList.stations[0].inverters);
            } else {
                let invs = stationList.stations.find( x => x.ID === Number(selectStation.station));
                console.log(selectStation.station, stationList.stations, invs);
                if (invs)
                    setInverters(invs.inverters);
            }

            setSelectStation((current) => {
                if (current.station === -1)
                    return { station: stationList.stations[0].ID, inverter: -1 }
                else
                    return current;
            });
        } else {
            if (!stationList || stationList.status === 0) {
                dispatch(stationListRequest());
            }
        };
    }, [stationList, dispatch]);

    const makeDrawData = useCallback(() => {
        let draw = [];
        let rec = stationHistory.history;
        if (rec.length === 0)
            return;

        rec.forEach(x => {
            if ('ACW' in x)
                x.ACW = Math.round(x.ACW / 10.0) / 100.0;
            if ('DCW' in x)
                x.DCW = Math.round(x.DCW / 10.0) / 100.0;

            if (x.RefHour) {
                draw.push({ ...x, 'title': String(x.RefHour) + '時' });
            } else if (x.RefDay) {
                draw.push({ ...x, 'title': String(x.RefDay) + '日' });
            } else {
                draw.push({ ...x, 'title': String(x.RefMonth) + '月' });
            }
        })
        setDrawData(draw);
    }, [stationHistory]);

    useEffect(() => {
        if (stationHistory.history)
            makeDrawData();
    }, [stationHistory, makeDrawData]);

    const handleStationChanged = (event) => {
        let new_station = stations.find(x => x.ID === event.target.value);
        if (new_station) {
            setSelectStation({ station: new_station.ID, inverter: -1 });
        }
    };

    const handleInverterChanged = (event) => {
        setSelectStation({ ...selectStation, inverter: event.target.value });
    };

    const handleYearChanged = (event) => {
        genDays(event.target.value, selectDate.month);
        setSelectDate({ ...selectDate, year: event.target.value, day: 0 });
    };

    const handleMonthChanged = (event) => {
        genDays(selectDate.year, event.target.value);
        setSelectDate({ ...selectDate, month: event.target.value, day: 0 });
    };

    const handleDayChanged = (event) => {
        setSelectDate({ ...selectDate, day: event.target.value });
    };

    const handleDataColumnChange = (event) => {
        setSelectColumn(event.target.value);
    };

    const handleQueryClicked = (event) => {
        if (event)
            event.preventDefault();
        let params = {};
        if (!selectColumn || selectColumn.length === 0) return;

        if (selectDate.year) params['year'] = selectDate.year;
        if (selectDate.month > 0) params['month'] = selectDate.month;
        if (selectDate.day > 0) params['day'] = selectDate.day;
        if (selectStation.inverter > -1) {
            params['gatewayID'] = inverters[selectStation.inverter].GatewayID;
            params['inverterID'] = inverters[selectStation.inverter].IndexID;
        }
        params['columns'] = selectColumn;
        drawType = 0;
        dispatch(stationHistoryRequest(selectStation.station, params));

    };

    const handleShowTypeChanged = (event) => {
        let draw = drawData.slice();
        if (event.target.checked) {
            drawType = 1;
            selectColumn.forEach(col => {
                let max = Math.max.apply(Math, draw.map(function (o) { return o[col]; }));
                let min = Math.min.apply(Math, draw.map(function (o) { return o[col]; }));
                draw.forEach(x => {
                    x[col + '_percent'] = ((x[col] - min) / (max - min)) * 100;
                });
            });
            setDrawData(draw);
        } else {
            drawType = 0;
            makeDrawData();
        }
    }

    let queryDisable = !(stationList && stationList.stations && stationList.stations.length > 0) || (stationHistory.status === 1);
    return (
        <Box m={2}>
            <Grid container spacing={1}>
                <Grid item container spacing={2}>
                    <Grid item xs={12} sm={12} md={5}>
                        <FormControl fullWidth className={style.select}>
                            <InputLabel id="station-select-label" >案場</InputLabel>
                            <Select variant="filled" labelId="station-select-label" classes={{ selectMenu: style.selectMenu }}
                                value={selectStation.station} onChange={handleStationChanged}>
                                {stations ? stations.map((station, index) => (
                                    <MenuItem key={index} value={station.ID}>{`${station.ID} : ${station.Name}  (${station.Description})`}</MenuItem>
                                )) : null}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={12} md={3}>
                        <FormControl fullWidth className={style.select}>
                            <InputLabel id="inv-select-label" >機台</InputLabel>
                            <Select variant="filled" labelId="inv-select-label" classes={{ selectMenu: style.selectMenu }}
                                value={selectStation.inverter} onChange={handleInverterChanged}>
                                <MenuItem key={0} dense value={-1}>所有機台</MenuItem>
                                {inverters ? inverters.map((inverter, index) => (
                                    <MenuItem key={index} value={index}>{`${inverter.IndexID} - ${inverter.Name}`}</MenuItem>
                                )) : null}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={4} md={2}>
                        <FormControl fullWidth className={style.select}>
                            <InputLabel id="year-select-label" >查詢日期</InputLabel>
                            <Select variant="filled" labelId="year-select-label" classes={{ selectMenu: style.selectMenu }}
                                value={selectDate.year} onChange={handleYearChanged}>
                                {yearOptions ? yearOptions.map((year, index) => (
                                    <MenuItem key={`year-${index}`} dense value={year}>{year}</MenuItem>
                                )) : null}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={4} md={1}>
                        <FormControl fullWidth className={style.select}>
                            <InputLabel id="month-select-label" ></InputLabel>
                            <Select variant="filled" labelId="month-select-label" classes={{ selectMenu: style.selectMenu }}
                                value={selectDate.month} onChange={handleMonthChanged} >
                                <MenuItem key={-1} dense value={0}>--</MenuItem>
                                {monthOptions ? monthOptions.map((month, index) => (
                                    <MenuItem key={index} dense value={month}>{month}</MenuItem>
                                )) : null}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={4} md={1}>
                        <FormControl fullWidth className={style.select}>
                            <InputLabel id="day-select-label" ></InputLabel>
                            <Select variant="filled" labelId="day-select-label" classes={{ selectMenu: style.selectMenu }}
                                value={selectDate.day} onChange={handleDayChanged} >
                                <MenuItem key={-1} dense value={0}>--</MenuItem>
                                {dayOptions ? dayOptions.map((day, index) => (
                                    <MenuItem key={index} dense value={day}>{day}</MenuItem>
                                )) : null}
                            </Select>
                        </FormControl>
                    </Grid>
                </Grid>
                <Grid item container spacing={2} alignItems="flex-end">
                    <Grid item xs={12} sm={12} md={8}>
                        <FormControl fullWidth className={style.select}>
                            <InputLabel id="data-select-label" >資料類型</InputLabel>
                            <Select variant="filled" multiple labelId="data-select-label" classes={{ selectMenu: style.selectMenu }} value={selectColumn}
                                onChange={handleDataColumnChange} renderValue={(selected) => (
                                    <div className={style.chips}>
                                        {selected.map((value) => (
                                            <Chip key={value} label={factors[value]} style={{ margin: 3, backgroundColor: 'white', fontSize: '1rem' }} />
                                        ))}
                                    </div>
                                )}>
                                {Object.keys(factors).map((item, index) => (
                                    <MenuItem key={index} dense value={item}>{factors[item]}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={12} md={1}>
                        <Button variant="contained" color="primary" fullWidth onClick={handleQueryClicked} disabled={queryDisable} >
                            查&nbsp;&nbsp;詢
                        </Button>
                    </Grid>
                </Grid>
            </Grid>
            <Grid container justify="center">
                <Grid item xs={12} md={11} lg={10}>
                    {stationList && stationList.status === 2 ?
                        stationHistory && stationHistory.status === 2 ?
                            <div style={{ textAlign: 'right', marginTop: 20, backgroundColor: 'rgba(255, 255, 255 , 0.8)', borderRadius: '10px', height: '70vh' }}>
                                {stationHistory.history.length > 0 ?
                                    <>
                                        <FormControlLabel label={<Box fontSize="0.5rem">以比例顯示</Box>}
                                            control={<Checkbox color="primary" size="small" value={drawType} onChange={handleShowTypeChanged} />} />
                                        <ResponsiveContainer height="90%">
                                            <LineChart 
                                                data={drawData}
                                                margin={{
                                                    top: 0, right: 30, left: 30, bottom: 30,
                                                }}>
                                                <CartesianGrid strokeDasharray="3 3" />
                                                <Legend iconType="rect" verticalAlign="top" height={64} />
                                                <XAxis label={{ value: '時間', position: 'bottom' }} interval={0} dataKey="title" tick={{ fontSize: 12 }} />
                                                <YAxis tick={{ fontSize: 12 }} />
                                                <Tooltip formatter={(value, name, props) => (Math.round(value * 100) / 100) + ' '} />
                                                {(stationHistory && stationHistory.select) ? stationHistory.select.map((item, index) => (
                                                    <Line key={index} unit={factors_unit[item]} type="monotone" name={factors[item]} dataKey={drawType ? item+'_percent': item} stackId={index} stroke={colorMap[index]} strokeWidth={2} fill={colorMap[index]} />
                                                )) : null}
                                            </LineChart >
                                        </ResponsiveContainer>
                                    </>
                                    : <Box textAlign="center" color="red" fontSize="h5.fontSize">沒有相關資料</Box>
                                }

                            </div>
                            : <Box pt={15}>
                                {stationHistory && stationHistory.status === 1 ?
                                    <LinearProgress />
                                    : stationHistory && stationHistory.status === 3 ?
                                        <Box textAlign="center" color="red" fontSize="h5.fontSize">{stationHistory.error}</Box>
                                        : null}
                            </Box>
                        :
                        <Box textAlign="center" color="red" fontSize="h5.fontSize">{stationList.error}</Box>
                    }
                </Grid>
            </Grid>
        </Box>
    );
};

export default StationQuery;